/**
 * Realtime Proxy WebSocket Hook
 * Connects to gc-realtime-proxy instead of directly to Azure OpenAI
 * This keeps Azure credentials secure on the server side
 */

import { useEffect, useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { store } from "@/store";
import {
  setConnecting,
  setConnected,
  setConnectionError,
  setSpeaking,
  setWaitingResponse,
  addMessage,
  updateUsage,
} from "@/store/slices/realtimeConversationSlice";
import { AudioPlayer } from "@/utils/audioPlayer";
import { base64ToFloat32 } from "@/utils/pcmEncoder";
import { logger } from "@/utils/logger";
import type { MeetingSection } from "@/types/meeting-section.types";
import { getProductConfig } from "@/config/product.config";
import { getProductLanguageConfig } from "@/config/language.config";

// Proxy URL - use environment variable or default to production
const PROXY_URL =
  import.meta.env.VITE_REALTIME_PROXY_URL ||
  "wss://gc-realtime-proxy.azurewebsites.net";

interface UseRealtimeProxyOptions {
  section: MeetingSection;
  onConversationEnd?: () => void;
}

interface RealtimeMessage {
  type: string;
  [key: string]: unknown;
}

export const useRealtimeProxy = (options: UseRealtimeProxyOptions) => {
  const { section, onConversationEnd } = options;
  const dispatch = useAppDispatch();

  const wsRef = useRef<WebSocket | null>(null);
  const audioPlayerRef = useRef<AudioPlayer | null>(null);
  const responseIdRef = useRef<string | null>(null);
  const audioChunksCountRef = useRef<number>(0);
  const hasRequestedInitialResponseRef = useRef<boolean>(false);
  const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const responseRequestedForCommitRef = useRef<boolean>(false);
  const audioUnlockHandlerRef = useRef<((event: Event) => void) | null>(null);

  const { isConnected, currentTurn, maxTurns } = useAppSelector(
    (state) => state.realtimeConversation
  );

  /**
   * Ensure AudioContext is ready for playback
   */
  const ensureAudioPlayerReady = useCallback(async () => {
    const player = audioPlayerRef.current;
    if (!player) return;

    try {
      await player.resume();
    } catch (error) {
      logger.warn("[Realtime] ⚠️ Failed to resume audio context:", error);
    }

    const state = player.getState();
    if (state === "running") return;

    if (typeof window === "undefined") return;

    if (!audioUnlockHandlerRef.current) {
      const handler = async () => {
        try {
          await player.resume();
          logger.log(
            "[Realtime] ✅ Audio context resumed after user interaction"
          );
        } catch (error) {
          logger.error("[Realtime] ❌ Failed to resume audio context:", error);
        } finally {
          if (audioUnlockHandlerRef.current) {
            window.removeEventListener(
              "pointerdown",
              audioUnlockHandlerRef.current
            );
            audioUnlockHandlerRef.current = null;
          }
        }
      };

      audioUnlockHandlerRef.current = handler;
      window.addEventListener("pointerdown", handler, { once: true });
      logger.warn(
        "[Realtime] ⚠️ Audio context locked - awaiting user interaction"
      );
    }
  }, []);

  /**
   * Play base64 audio chunk
   */
  const playBase64Audio = useCallback(
    async (base64Audio: string, context: string) => {
      if (!audioPlayerRef.current) {
        logger.warn("[Realtime] ⚠️ Ignoring audio chunk - player not ready");
        return;
      }

      dispatch(setSpeaking(true));

      try {
        await ensureAudioPlayerReady();
        const audioData = base64ToFloat32(base64Audio);
        logger.log(
          `[Realtime] 🔊 Audio chunk from ${context}: ${audioData.length} samples`
        );
        audioPlayerRef.current
          .playChunk(audioData)
          .catch((error) =>
            logger.error(`[Realtime] ❌ Playback error (${context}):`, error)
          );
      } catch (error) {
        logger.error(`[Realtime] ❌ Failed to play audio (${context}):`, error);
      }
    },
    [dispatch, ensureAudioPlayerReady]
  );

  /**
   * Handle output audio content from response
   */
  const handleOutputAudioContent = useCallback(
    (contentParts: unknown, source: string) => {
      if (!Array.isArray(contentParts) || contentParts.length === 0) return;

      contentParts.forEach((part, index) => {
        if (
          part &&
          typeof part === "object" &&
          (part as { type?: string }).type === "output_audio"
        ) {
          const audio = (part as { audio?: unknown }).audio;
          const format = (part as { format?: unknown }).format;

          if (typeof audio !== "string") return;
          if (format && format !== "pcm16") return;

          void playBase64Audio(audio, `${source}#${index}`);
        }
      });
    },
    [playBase64Audio]
  );

  /**
   * Initialize AudioPlayer (must be called from user gesture)
   */
  const initializeAudio = useCallback(async () => {
    if (!audioPlayerRef.current) {
      logger.log("[Realtime] 🔊 Initializing AudioPlayer...");
      audioPlayerRef.current = new AudioPlayer();
    }
    await ensureAudioPlayerReady();
  }, [ensureAudioPlayerReady]);

  /**
   * Request response from Azure via proxy
   */
  const requestResponseCreate = useCallback((reason: string) => {
    if (responseRequestedForCommitRef.current) {
      logger.log(`[Realtime] ⏭️ Skipping response.create - already requested`);
      return;
    }

    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      logger.warn("[Realtime] ⚠️ Cannot send response.create - not connected");
      return;
    }

    wsRef.current.send(JSON.stringify({ type: "response.create" }));
    responseRequestedForCommitRef.current = true;
    logger.log(`[Realtime] 🎤 response.create sent (${reason})`);
  }, []);

  /**
   * Handle messages from the proxy (forwarded from Azure)
   */
  const handleRealtimeMessage = useCallback(
    async (message: RealtimeMessage) => {
      const state = store.getState();
      const { currentTurn, maxTurns } = state.realtimeConversation;

      logger.log("[Realtime] 🔄 Message:", message.type, {
        turn: currentTurn,
        maxTurns,
      });

      switch (message.type) {
        // Proxy-specific messages
        case "connection_opened":
          logger.log("[Realtime] ✅ Proxy connected to Azure OpenAI");
          break;

        case "interview_completed": {
          logger.log("[Realtime] ✅ Interview completed by proxy");
          // Wait for audio to finish before calling onConversationEnd
          const playerForComplete = audioPlayerRef.current;
          if (playerForComplete) {
            const remainingForComplete =
              playerForComplete.getRemainingPlaybackTime();
            const waitForComplete = Math.max(remainingForComplete + 1500, 2000);
            logger.log(
              `[Realtime] ⏳ Will navigate after ${waitForComplete}ms (audio remaining: ${remainingForComplete}ms)`
            );
            setTimeout(() => onConversationEnd?.(), waitForComplete);
          } else {
            setTimeout(() => onConversationEnd?.(), 2000);
          }
          break;
        }

        // Azure OpenAI messages (forwarded by proxy)
        case "session.created":
          logger.log("[Realtime] Session created");
          break;

        case "session.updated":
          logger.log("[Realtime] Session updated");
          dispatch(setConnected(true));
          if (!hasRequestedInitialResponseRef.current && wsRef.current) {
            dispatch(setWaitingResponse(true));
            requestResponseCreate("session.updated initial");
            hasRequestedInitialResponseRef.current = true;
          }
          break;

        case "response.created":
          logger.log("[Realtime] 🎤 Response created:", message.response);
          responseIdRef.current =
            (message.response as { id?: string })?.id || null;
          dispatch(setWaitingResponse(true));
          break;

        case "response.audio.delta":
          if (message.delta && audioPlayerRef.current) {
            dispatch(setSpeaking(true));
            try {
              await ensureAudioPlayerReady();
              const audioData = base64ToFloat32(message.delta as string);
              audioPlayerRef.current
                .playChunk(audioData)
                .catch((error) =>
                  logger.error("[Realtime] Error playing audio:", error)
                );
            } catch (error) {
              logger.error("[Realtime] Error playing audio delta:", error);
            }
          }
          break;

        case "response.audio_transcript.done":
          logger.log("[Realtime] Transcript done:", message.transcript);
          dispatch(
            addMessage({
              id: responseIdRef.current || `assistant-${Date.now()}`,
              role: "assistant",
              content: (message.transcript as string) || "",
              timestamp: Date.now(),
            })
          );
          break;

        case "response.output_item.done":
          if (message.item) {
            handleOutputAudioContent(
              (message.item as { content?: unknown }).content,
              "response.output_item.done"
            );
          }
          break;

        case "response.audio.done":
          logger.log("[Realtime] ✅ Audio streaming complete");
          setTimeout(() => {
            dispatch(setSpeaking(false));
            dispatch(setWaitingResponse(false));
          }, 200);

          if (currentTurn >= maxTurns && maxTurns > 0) {
            logger.log("[Realtime] ✅ Last interaction - waiting for playback");
            const audioPlayer = audioPlayerRef.current;
            if (audioPlayer) {
              const remainingTime = audioPlayer.getRemainingPlaybackTime();
              const waitTime = Math.max(remainingTime + 1500, 2000);
              logger.log(
                `[Realtime] ⏳ Will call onConversationEnd in ${waitTime}ms`
              );
              setTimeout(() => onConversationEnd?.(), waitTime);
            } else {
              setTimeout(() => onConversationEnd?.(), 3000);
            }
          }
          break;

        case "response.done":
          logger.log("[Realtime] Response done");
          if (message.response) {
            const usage = (message.response as { usage?: unknown }).usage;
            if (usage) {
              dispatch(
                updateUsage(
                  usage as {
                    total_tokens: number;
                    input_tokens: number;
                    output_tokens: number;
                  }
                )
              );
            }
          }
          break;

        case "conversation.item.input_audio_transcription.completed":
          logger.log("[Realtime] 📝 User transcript:", message.transcript);
          dispatch(
            addMessage({
              id: (message.item_id as string) || `user-${Date.now()}`,
              role: "user",
              content: (message.transcript as string) || "",
              timestamp: Date.now(),
            })
          );
          break;

        case "input_audio_buffer.committed":
          logger.log("[Realtime] ✅ Audio buffer committed");
          if (commitTimeoutRef.current) {
            clearTimeout(commitTimeoutRef.current);
            commitTimeoutRef.current = null;
          }
          requestResponseCreate("server commit confirmation");
          break;

        case "error":
          logger.error("[Realtime] ❌ Error:", message.error || message);
          dispatch(
            setConnectionError(
              (message.message as string) || "Azure OpenAI error"
            )
          );
          break;

        default:
          logger.log("[Realtime] Unhandled:", message.type);
          break;
      }
    },
    [
      dispatch,
      onConversationEnd,
      handleOutputAudioContent,
      ensureAudioPlayerReady,
      requestResponseCreate,
    ]
  );

  /**
   * Connect to the realtime proxy
   */
  const connect = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      logger.log("[Realtime] Already connected");
      return;
    }

    try {
      dispatch(setConnecting(true));
      dispatch(setWaitingResponse(true));

      // Initialize audio player FIRST (before WebSocket)
      // This ensures audio is ready when messages arrive
      if (!audioPlayerRef.current) {
        logger.log(
          "[Realtime] 🔊 Initializing AudioPlayer before WebSocket..."
        );
        audioPlayerRef.current = new AudioPlayer();
        await ensureAudioPlayerReady();
        logger.log("[Realtime] ✅ AudioPlayer ready");
      }

      // Build proxy WebSocket URL
      // Optionally include session token for authentication
      const sessionToken = localStorage.getItem("session_token");
      const wsUrl = sessionToken
        ? `${PROXY_URL}/realtime-interview?token=${encodeURIComponent(sessionToken)}`
        : `${PROXY_URL}/realtime-interview`;

      logger.log("[Realtime] Connecting to proxy:", wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // CRITICAL: Register event handlers IMMEDIATELY after WebSocket creation
      // to avoid race conditions where events fire before handlers are set
      ws.onopen = () => {
        logger.log("[Realtime] ✅ WebSocket OPEN - readyState:", ws.readyState);
        audioChunksCountRef.current = 0;
        hasRequestedInitialResponseRef.current = false;

        // Send config to proxy - this triggers Azure connection
        const productConfig = getProductConfig();
        const { realtimeTranscriptionLanguage } = getProductLanguageConfig(
          productConfig.id
        );
        const configMessage = {
          type: "config",
          config: {
            voice: section.voice || "alloy",
            num_interactions: section.num_interactions || 2,
            instructions: section.instructions,
            temperature: section.temperature,
            max_response_output_tokens: Math.min(
              section.max_response_output_tokens || 4096,
              4096
            ),
            language: realtimeTranscriptionLanguage,
          },
        };

        const configJson = JSON.stringify(configMessage);
        logger.log("[Realtime] 📤 Sending config to proxy...");
        logger.log("[Realtime] 📤 Config content:", configMessage.config);

        try {
          ws.send(configJson);
          logger.log(
            "[Realtime] ✅ ws.send() completed, readyState:",
            ws.readyState,
            "bufferedAmount:",
            ws.bufferedAmount
          );
        } catch (sendError) {
          logger.error("[Realtime] ❌ ws.send() FAILED:", sendError);
        }
      };

      ws.onmessage = (event) => {
        logger.log(
          "[Realtime] 📨 RAW message received, data length:",
          event.data?.length || 0
        );
        try {
          const message: RealtimeMessage = JSON.parse(event.data);
          logger.log("[Realtime] 📩 Parsed message type:", message.type);
          if (message.type !== "response.audio_transcript.delta") {
            logger.log("[Realtime] 📩 From proxy:", message.type);
          }
          // Handle message and catch any errors
          handleRealtimeMessage(message).catch((err) => {
            logger.error("[Realtime] ❌ Error in handleRealtimeMessage:", err);
          });
        } catch (error) {
          logger.error(
            "[Realtime] ❌ Failed to parse message:",
            error,
            "Raw data:",
            event.data?.substring?.(0, 200)
          );
        }
      };

      ws.onerror = (error) => {
        logger.error("[Realtime] WebSocket error:", error);
        dispatch(setConnectionError("Connection error occurred"));
      };

      ws.onclose = (event) => {
        logger.log("[Realtime] WebSocket closed:", event.code, event.reason);
        dispatch(setConnected(false));

        const audioPlayer = audioPlayerRef.current;
        if (audioPlayer) {
          const remaining = audioPlayer.getRemainingPlaybackTime();
          if (remaining > 0) {
            logger.log(
              `[Realtime] ⏳ Waiting ${remaining}ms for audio to finish`
            );
            setTimeout(() => {
              audioPlayer.close();
              audioPlayerRef.current = null;
            }, remaining + 500);
          } else {
            audioPlayer.close();
            audioPlayerRef.current = null;
          }
        }
      };
    } catch (error) {
      logger.error("[Realtime] Failed to connect:", error);
      dispatch(
        setConnectionError(
          error instanceof Error ? error.message : "Connection failed"
        )
      );
    }
  }, [section, dispatch, ensureAudioPlayerReady, handleRealtimeMessage]);

  /**
   * Send audio chunk to proxy (forwarded to Azure)
   */
  const sendAudioChunk = useCallback((base64Audio: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      audioChunksCountRef.current++;
      wsRef.current.send(
        JSON.stringify({
          type: "input_audio_buffer.append",
          audio: base64Audio,
        })
      );
      logger.log(
        `[Realtime] 📤 Audio chunk #${audioChunksCountRef.current} sent`
      );
    } else {
      logger.error("[Realtime] ❌ WebSocket not open");
    }
  }, []);

  /**
   * Commit audio buffer and request response
   */
  const commitAudio = useCallback(() => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      logger.error("[Realtime] ❌ Cannot commit - not connected");
      return;
    }

    const chunksCount = audioChunksCountRef.current;
    if (chunksCount === 0) {
      logger.error("[Realtime] ❌ Cannot commit empty buffer");
      return;
    }

    responseRequestedForCommitRef.current = false;

    wsRef.current.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
    logger.log(`[Realtime] ✅ Committed ${chunksCount} chunks`);

    requestResponseCreate("post-commit");

    // Timeout detector
    if (commitTimeoutRef.current) clearTimeout(commitTimeoutRef.current);
    commitTimeoutRef.current = setTimeout(() => {
      logger.error("[Realtime] ❌ Timeout: no response after 5s");
      if (
        !responseRequestedForCommitRef.current &&
        wsRef.current?.readyState === WebSocket.OPEN
      ) {
        wsRef.current.send(JSON.stringify({ type: "response.create" }));
        responseRequestedForCommitRef.current = true;
      }
      commitTimeoutRef.current = null;
    }, 5000);

    audioChunksCountRef.current = 0;
  }, [requestResponseCreate]);

  /**
   * Prime audio output
   */
  const primeAudioOutput = useCallback(async () => {
    if (audioPlayerRef.current) {
      await audioPlayerRef.current.resume();
    }
  }, []);

  /**
   * Reset audio routing (iOS fix for silent mode)
   */
  const resetAudioRouting = useCallback(async () => {
    if (audioPlayerRef.current) {
      await audioPlayerRef.current.resetRouting();
    }
  }, []);

  /**
   * Disconnect from proxy
   */
  const disconnect = useCallback(() => {
    logger.log("[Realtime] Disconnecting...");

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const audioPlayer = audioPlayerRef.current;
    if (audioPlayer) {
      const remaining = audioPlayer.getRemainingPlaybackTime();
      if (remaining > 0) {
        logger.log(
          `[Realtime] ⏳ Waiting ${remaining}ms for audio before disconnect`
        );
        setTimeout(() => {
          audioPlayer.close();
          audioPlayerRef.current = null;
        }, remaining + 500);
      } else {
        audioPlayer.close();
        audioPlayerRef.current = null;
      }
    }

    if (typeof window !== "undefined" && audioUnlockHandlerRef.current) {
      window.removeEventListener("pointerdown", audioUnlockHandlerRef.current);
      audioUnlockHandlerRef.current = null;
    }
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => disconnect();
  }, [disconnect]);

  return {
    connect,
    disconnect,
    sendAudioChunk,
    commitAudio,
    primeAudioOutput,
    initializeAudio,
    resetAudioRouting,
    isConnected,
    audioPlayer: audioPlayerRef.current,
  };
};
